import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Download, HardDriveUpload, Loader2, Maximize, PencilLine, ZoomIn, ZoomOut, X } from "lucide-react";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import { BoardStore, boardFileName, renderBoardPng } from "./boardModel";
import { WhiteboardCanvas, type WhiteboardCanvasHandle } from "./WhiteboardCanvas";
import { downloadBlob, saveBoardToDrive, type BoardDriveSave } from "./whiteboardExport";
import { useIsDark } from "./useIsDark";

/**
 * The meeting's whiteboard after (or outside) the meeting — the Recap's
 * "Whiteboard" tab. Read-only: pan and zoom, download it as a PNG or save a
 * copy to your Drive shared with the meeting.
 */
export function WhiteboardViewer({ meetingId, meetingSubject, className }: { meetingId: string; meetingSubject: string; className?: string }) {
    const token = useAccessToken();
    const auth = useAuth();
    const dark = useIsDark();
    const store = useMemo(() => new BoardStore(), []);
    const canvasRef = useRef<WhiteboardCanvasHandle>(null);
    const [busy, setBusy] = useState<"drive" | "png" | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [driveResult, setDriveResult] = useState<BoardDriveSave | null>(null);
    const board = useQuery({
        queryKey: ["meeting-whiteboard", meetingId, token],
        queryFn: () => connectParityApi.meetingWhiteboard(meetingId, token),
        enabled: Boolean(token),
        staleTime: 15_000
    });

    useEffect(() => {
        if (!board.data) return;
        store.load(board.data.objects ?? []);
        requestAnimationFrame(() => canvasRef.current?.fit());
    }, [board.data, store]);

    const count = board.data?.objects.length ?? 0;

    const download = async () => {
        setBusy("png");
        setMessage(null);
        try {
            downloadBlob(await renderBoardPng(store.list()), boardFileName(meetingSubject));
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Couldn't export the whiteboard.");
        } finally {
            setBusy(null);
        }
    };
    const save = async () => {
        setBusy("drive");
        setMessage(null);
        setDriveResult(null);
        try {
            setDriveResult(await saveBoardToDrive({
                blob: await renderBoardPng(store.list()),
                fileName: boardFileName(meetingSubject),
                meetingId,
                myEmail: auth.user?.profile.email,
                token
            }));
        } catch (err) {
            setMessage(err instanceof Error ? `Couldn't save to Drive: ${err.message}` : "Couldn't save to Drive.");
        } finally {
            setBusy(null);
        }
    };

    if (board.isPending) {
        return <p className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}><Loader2 className="h-3 w-3 animate-spin" /> Loading the whiteboard…</p>;
    }
    if (board.isError) {
        return <p role="alert" className={cn("text-sm text-destructive", className)}>Couldn't load the whiteboard.</p>;
    }
    if (count === 0) {
        return (
            <p className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
                <PencilLine className="h-4 w-4" aria-hidden /> Nothing was drawn on this meeting's whiteboard.
            </p>
        );
    }

    const btn = "inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";
    const iconBtn = "flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";
    return (
        <div className={cn("space-y-2", className)}>
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => void download()} disabled={busy !== null} className={btn}>
                    {busy === "png" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PNG
                </button>
                <button type="button" onClick={() => void save()} disabled={busy !== null} className={btn}>
                    {busy === "drive" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <HardDriveUpload className="h-3.5 w-3.5" />} Save to Drive
                </button>
                <span className="text-[11px] text-muted-foreground">
                    {count} item{count === 1 ? "" : "s"}
                    {board.data?.updatedAt ? ` · last change ${new Date(board.data.updatedAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}` : ""}
                    {board.data?.live ? " · in use now" : ""}
                </span>
            </div>
            {message ? <p role="alert" className="text-xs text-destructive">{message}</p> : null}
            {driveResult ? (
                <p role="status" className="flex flex-wrap items-center gap-2 text-xs">
                    {driveResult.warning ?? "Saved to your Drive and shared with the meeting."}
                    <a href={driveResult.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline">Open in Drive</a>
                    <button type="button" onClick={() => setDriveResult(null)} aria-label="Dismiss" className="rounded p-0.5 hover:bg-accent"><X className="h-3 w-3" /></button>
                </p>
            ) : null}
            <div className="relative h-[min(60vh,420px)] min-h-[260px] overflow-hidden rounded-md border">
                <WhiteboardCanvas
                    ref={canvasRef}
                    store={store}
                    dark={dark}
                    readOnly
                    tool="hand"
                    color="#1f2937"
                    size={4}
                    className="h-full w-full"
                    ariaLabel={`Whiteboard from ${meetingSubject} (view only, ${count} items)`}
                />
                <div className="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-lg border bg-card p-0.5 shadow" role="group" aria-label="Zoom">
                    <button type="button" onClick={() => canvasRef.current?.zoomBy(1 / 1.2)} aria-label="Zoom out" className={iconBtn}><ZoomOut className="h-4 w-4" /></button>
                    <button type="button" onClick={() => canvasRef.current?.zoomBy(1.2)} aria-label="Zoom in" className={iconBtn}><ZoomIn className="h-4 w-4" /></button>
                    <button type="button" onClick={() => canvasRef.current?.fit()} aria-label="Fit the board" className={iconBtn}><Maximize className="h-4 w-4" /></button>
                </div>
            </div>
        </div>
    );
}
